import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { checkSkuFormWithFallback } from "services/Api/checkSkuFetch";
import { submitScanPointWithFallback } from "services/Api/scanPointFetch";
import {
  isNewApiEnabled,
  loadCustomerProfileToStore,
} from "services/Api/customerProfileFetch";
import {
  fetchStreakProgress,
  fetchStreakCampaignActive,
  loadStreakProgressToStore,
} from "services/Api/streakProgressFetch";
import { fetchBuyMoreGetMoreActive } from "services/Api/buyMoreGetMoreFetch";
import { normalizeStreakChallenge } from "../../utils/streakDisplay";
import config from "util/config";
import Menu from "pages/menuMain/menu";
import _ from "lodash";
import ModalContext from "contexts/ModalContext";
import CollectpointHeader from "../centerComponent/collectPointHeader";
import ScanOCR from "utility/ocr";
import { setProfile } from "features/ProfileSlice";
import UploadReceipt from "./uploadReceipt";

import PointMission from "../../assets/images/scanpoint/point_mission.webp";
import HowToPopup from "../../components/howtoPopup";
import StreakHowToPopup from "../../components/StreakHowToPopup";
import StreakChallengeBanner, {
  resolveStreakBannerStampCount,
  resolveStreakShowTodayRewardOverlay,
} from "../../components/StreakChallengeBanner";
import { resolveStreakPopupType } from "../../components/StreakRewardPopup";

const DEF_FORM_VALUE = [""];
const DEF_FORM_ERROR = [3];
const IS_DEV_MODE = process.env.REACT_APP_DEV_MODE === "true";

const ScanPointNew = () => {
  const { mAlert } = useContext(ModalContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formValues, setFormValues] = useState(DEF_FORM_VALUE);
  const [showHowToPopup, setShowHowToPopup] = useState(false);
  const [showStreakHowToPopup, setShowStreakHowToPopup] = useState(false);
  const [showBuyMoreGetMoreBanner, setShowBuyMoreGetMoreBanner] =
    useState(false);
  const [showStreakChallengeBanner, setShowStreakChallengeBanner] =
    useState(false);
  const [formErrors, setFormErrors] = useState(DEF_FORM_ERROR);
  const [disabledForm, setDisabledForm] = useState(false); // ปุ่มกด
  const [isLoading, setIsLoading] = useState(false);
  const [lastSubmitSource, setLastSubmitSource] = useState(null);

  // profile state กลาง
  const stateProfile = useSelector((state) => state.profile.dataUser);
  const customerId =
    stateProfile.customerId || stateProfile.id || stateProfile.dataUser?.id;

  const streakBannerStampCount = resolveStreakBannerStampCount(stateProfile);
  const showStreakTodayRewardOverlay =
    resolveStreakShowTodayRewardOverlay(stateProfile);

  useEffect(() => {
    let cancelled = false;

    const refreshBanners = async () => {
      if (!isNewApiEnabled()) {
        if (!cancelled) {
          setShowBuyMoreGetMoreBanner(false);
          setShowStreakChallengeBanner(false);
        }
        return;
      }

      const [bmgmRes, streakRes] = await Promise.all([
        fetchBuyMoreGetMoreActive(),
        fetchStreakCampaignActive(),
      ]);
      if (cancelled) {
        return;
      }

      setShowBuyMoreGetMoreBanner(Boolean(bmgmRes.active));
      setShowStreakChallengeBanner(Boolean(streakRes.active));

      if (IS_DEV_MODE) {
        console.log("[ScanpointNew] campaign banners", {
          bmgm: bmgmRes,
          streak: streakRes,
        });
      }
    };

    refreshBanners();
    const intervalId = setInterval(refreshBanners, 10000);
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshBanners();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refreshBanners);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refreshBanners);
    };
  }, []);

  useEffect(() => {
    if (!customerId || !isNewApiEnabled()) return;

    (async () => {
      const profileRes = await loadCustomerProfileToStore({
        dispatch,
        customerId,
      });

      const streakRes = await loadStreakProgressToStore({
        dispatch,
        customerId,
        existingPointView:
          profileRes.result?.pointView ?? stateProfile.pointView,
      });

      if (IS_DEV_MODE) {
        console.log("[ScanpointNew] streak loaded", {
          customerId,
          streakRes,
          bannerStampCount: resolveStreakBannerStampCount({
            ...stateProfile,
            pointView: {
              ...(profileRes.result?.pointView ?? stateProfile.pointView),
              streakChallenge: streakRes.streakChallenge,
            },
          }),
        });
      }
    })();
  }, [customerId, dispatch]);

  // camera
  const [openCamera, setOpenCamera] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const addRowForm = () => {
    // VALUE
    let _formValues = _.cloneDeep(formValues);
    _formValues.push("");
    setFormValues(_formValues);

    // ERROR
    let _formErrors = _.cloneDeep(formErrors);
    _formErrors.push(3);
    setFormErrors(_formErrors);
  };

  const removeRowForm = (form = [], errorForm, index, value = "") => {
    let _form = _.cloneDeep(form);
    let _errorForm = _.cloneDeep(errorForm);

    let tmpValue = value ?? _form[index];
    if (tmpValue === "") {
      if (!value) {
        _errorForm = _errorForm.filter((item, i) => i !== index);
      }
    } else if (
      !(
        value.length == 13 ||
        value.length == 14 ||
        value.length == 15 ||
        value.length == 16
      )
    ) {
      _errorForm[index] = 3;
    } else {
      _errorForm[index] = 0;
    }
    return _errorForm;
  };

  const editFormValues = (form = [], value = "", index) => {
    let _form = _.cloneDeep(form);
    _form[index] = value;
    return _form;
  };

  const clearFormValues = (form = [], index) => {
    let _form = _.cloneDeep(form);
    if (_form[index]) {
      _form[index] = "";
    } else {
      _form = _form.filter((item, i) => i !== index);
    }
    return _form;
  };

  const validateForm = (notRequire = false) => {
    let isPass = true;
    const _formValues = _.cloneDeep(formValues);
    for (let i in _formValues) {
      const value = _formValues[i];

      if (notRequire === false) {
        // มี option แบบไม่เช็ค require
        if (!value) {
          // เช็ค require
          isPass = false;
          break;
        }
      }

      if (
        !(
          value.length == 13 ||
          value.length == 14 ||
          value.length == 15 ||
          value.length == 16
        )
      ) {
        isPass = false;
        break;
      }

      isPass = true;
    }
    // setDisabledForm(!isPass);
    return !isPass;
  };

  const postPointHistory = async (payload) => {
    let _return = {
      status: false,
      result: null,
      error: null,
      source: "legacy",
    };
    try {
      const response = await submitScanPointWithFallback({ data: payload });
      _return.source = response.source;
      if (response.status) {
        _return.status = true;
        _return.result = response.result;
        console.log("[ScanpointNew] submit success via", response.source, {
          point: response.result?.point,
          currentPoint: response.result?.currentPoint,
        });
      } else {
        _return.error = response.error;
        console.error("[ScanpointNew] submit failed:", {
          payload,
          response: response.error,
          source: response.source,
        });
        const data = response.error;
        let _title = "เกิดข้อผิดพลาด",
          _subTitle =
            data?.resultDesc ||
            data?.resultDescription ||
            data?.resultMsg ||
            "กรุณาลองใหม่อีกครั้ง";
        mAlert.info({
          open: true,
          title: _title,
          subTitle: _subTitle,
          content: "",
          onExit: () => {},
        });
      }
    } catch (e) {
      _return.error = e;
      console.error("[ScanpointNew] submit exception:", {
        payload,
        error: e?.response?.data || e?.message || e,
      });
      console.log("ERR : ", e);
    }
    return _return;
  };

  const prepareData = () => {
    const codeSkuData = _.cloneDeep(formValues);
    return {
      customerId: customerId,
      pointType: 1,
      codeSku: codeSkuData,
    };
  };

  const savePointHistory = async () => {
    setIsLoading(true);
    const payload = prepareData();
    const result = await postPointHistory(payload);
    setLastSubmitSource(result.source);
    if (result.status) {
      const scanResult = result.result;
      const profileCustomerId = stateProfile.customerId || customerId;
      const previousDisplayStreak = resolveStreakBannerStampCount(stateProfile);

      const streakPopupType = isNewApiEnabled()
        ? resolveStreakPopupType(scanResult, previousDisplayStreak)
        : null;

      let streakChallengeForCollect = null;
      if (profileCustomerId && isNewApiEnabled()) {
        const streakRes = await fetchStreakProgress({
          customerId: profileCustomerId,
        });
        if (streakRes.status && streakRes.streakChallenge) {
          streakChallengeForCollect = normalizeStreakChallenge(
            streakRes.streakChallenge,
          );
        }
      }

      dispatch(
        setProfile({
          currentPoint: scanResult.currentPoint,
          actionPoint: scanResult.point,
          countSticker: scanResult.countSticker,
          stickerHistory: scanResult.stickerHistory,
          actionHeart: scanResult.heartHistory?.amount ?? 0,
          currentHeart: scanResult.heartHistory?.currentHeart ?? 0,
          isSchedualGame: scanResult.isSchedualGame,
          isSchedualSticker: scanResult.isSchedualSticker,
          // for wordbook
          // wordBookNormalWord: scanResult.normal,
          // wordBookIsLucky: scanResult.lucky.length > 0 ? true : false,
          // wordBookLuckyWord: scanResult.lucky,
        }),
      );

      setIsLoading(false);
      navigate("/collectpoint", {
        state: {
          ...(streakPopupType ? { streakPopupType } : {}),
          ...(streakChallengeForCollect
            ? { streakChallenge: streakChallengeForCollect }
            : {}),
        },
      });

      // for wordbook
      // if (result.result.normal) {
      //   setIsLoading(false);
      //   navigate("/random-word");
      // } else if (
      //   result.result.isSchedualSticker == 1 ||
      //   result.result.isSchedualGame == 1
      // ) {
      //   setIsLoading(false);
      //   navigate("/character-process");
      // } else {
      //   setIsLoading(false);
      //   navigate("/collectpoint");
      // }
    } else {
      console.error("[ScanpointNew] savePointHistory not success:", {
        payload,
        result,
      });
      setIsLoading(false);
    }
  };

  const postCheckCodeForm = async (payload) => {
    const result = await checkSkuFormWithFallback({ data: payload });
    return {
      status: result.status,
      result: result.result ?? [],
      source: result.source,
    };
  };

  const checkSkuFormAll = async (form = []) => {
    const payload = {
      codeSku: formValues,
    };
    const result = await postCheckCodeForm(payload);
    if (result.status) {
      const objSkuStatus = result.result;
      let _formValues = _.cloneDeep(formValues);
      let _formErrors = _.cloneDeep(formErrors);

      _formErrors = _formValues.map((item) => objSkuStatus[item]);
      setFormErrors(_formErrors);
    }
  };

  const checkDupSkuForm = (form = [], error = []) => {
    const _form = _.cloneDeep(form);
    const _error = _.cloneDeep(error);
    let isPass = true;
    for (let i in _form) {
      let v = form[i];
      let findDup = _.cloneDeep(form).filter((item, index) => item == v);
      if (findDup.length > 1) {
        isPass = false;
      }
    }
    return isPass;
  };

  const onSubmit = () => {
    savePointHistory();
  };

  useEffect(() => {
    const timeOutId = setTimeout(() => checkSkuFormAll(), 400); // ยิง API หลังจาก พิมเสด 1 วิ
    return () => clearTimeout(timeOutId);
  }, [formValues]);

  const contBTN0 = validateForm();
  const contBTN1 = !(
    formErrors.filter((item) => item === 0).length === formErrors.length
  );
  const contBTN2 = !checkDupSkuForm(formValues);
  const disabledBTN = contBTN0 || contBTN1 || contBTN2;
  const newApiUrl = config.config().NEW_API_URL;
  const usingNewApi = isNewApiEnabled();
  return (
    <div>
      <Helmet>
        <title>โคอะลา มาร์ช</title>
        <meta name="title" content="โคอะลา มาร์ช" />
        <meta name="description" content="" />
        <meta name="keyword" content="" />
        <meta property="og:title" content="โคอะลา มาร์ช" />
        <meta property="og:description" content="" />
        <meta property="og:image" content="" />
        <meta property="og:url" content="" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="" />
      </Helmet>

      <div id="pageScan">
        <main>
          <div className="wrapContent">
            <CollectpointHeader />
            <section id="pScanCode">
              {/* <!-- เปิดตอน Phase 2 -->
            <!-- <div className="bLink">
              <a href="artcollection.php">สุ่ม Sticker collection</a>
            </div> --> */}
              <div className="bScanCode">
                {showBuyMoreGetMoreBanner && (
                  <div style={{ position: "relative", marginBottom: "20px" }}>
                    <img
                      src={PointMission}
                      alt="PointMission"
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                      }}
                    />
                    <div
                      onClick={() => setShowHowToPopup(true)}
                      style={{
                        position: "absolute",
                        bottom: "4%",
                        left: "30%",
                        width: "40%",
                        height: "18%",
                        cursor: "pointer",
                        borderRadius: "30px",
                      }}
                    ></div>
                  </div>
                )}
                {showStreakChallengeBanner && (
                  <StreakChallengeBanner
                    currentStreak={streakBannerStampCount}
                    showTodayRewardOverlay={showStreakTodayRewardOverlay}
                    onHowToClick={() => setShowStreakHowToPopup(true)}
                  />
                )}
                <h2>Scan point หรือกรอกรหัส</h2>
                <div className="bForm">
                  {formValues.map((item, index) => {
                    return (
                      <div className="bRow" key={index}>
                        <a
                          className="btnScan"
                          onClick={() => {
                            setOpenCamera(true);
                            setCurrentIndex(index);
                          }}
                        ></a>
                        <div
                          className={`control-group 
                          ${
                            formErrors[index] === 0
                              ? "success"
                              : [1, 2, 4].includes(formErrors[index])
                                ? "error"
                                : ""
                          }`}
                        >
                          {/* <!-- ถ้า Validate ไม่ผ่านให้ใส่ Class error / ถ้าผ่่าน ให้ใส่ Class success --> */}
                          <div>
                            <input
                              type="text"
                              placeholder="กรอกรหัสฝากล่อง"
                              value={formValues[index]}
                              onChange={(e) => {
                                console.log("e >>", e.target.value);
                                const updateForm = editFormValues(
                                  formValues,
                                  e.target.value,
                                  index,
                                );
                                const deleteError = removeRowForm(
                                  formValues,
                                  formErrors,
                                  index,
                                  e.target.value,
                                );
                                setFormValues(updateForm);
                                setFormErrors(deleteError);
                              }}
                              maxLength={16}
                            />
                            {index === 0 ? (
                              <></>
                            ) : (
                              <a
                                className="btnClear"
                                onClick={() => {
                                  const deleteForm = clearFormValues(
                                    formValues,
                                    index,
                                  );
                                  const deleteError = removeRowForm(
                                    formValues,
                                    formErrors,
                                    index,
                                  );
                                  setFormValues(deleteForm);
                                  setFormErrors(deleteError);
                                }}
                              ></a>
                            )}
                          </div>
                          <label htmlFor="" className="tError">
                            {formErrors[index] === 1 &&
                              "*รหัสนี้ถูกใช้งานไปแล้ว"}
                            {formErrors[index] === 2 && "*รหัสไม่ถูกต้อง"}
                          </label>
                        </div>
                      </div>
                    );
                  })}

                  <div className="bRow">
                    {formValues.length < 5 && (
                      <div
                        className="btnAdd"
                        onClick={() => addRowForm()}
                      ></div>
                    )}
                  </div>
                  <div className="bBtn">
                    <button
                      className="btn"
                      onClick={onSubmit}
                      disabled={disabledBTN || isLoading}
                    >
                      สะสมคะแนน
                    </button>
                  </div>
                </div>
              </div>
            </section>
            <UploadReceipt />

            {openCamera ? (
              <ScanOCR
                // getCodePoint={handleRecognizedTextChange}
                onCapture={(value) => {
                  let _formValues = _.cloneDeep(formValues);
                  _formValues[currentIndex] = `${value}`.trim();
                  setFormValues(_formValues);

                  setOpenCamera(false);
                }}
                onClose={(value) => {
                  setOpenCamera(value);
                }}
              />
            ) : (
              <div></div>
            )}

            {/* <div className="apiScan"><div><div style="width: 50%; height: 12%; position: relative; border: 1px solid rgba(255, 0, 0, 0.7); margin: auto;"></div></div><video autoplay="" playsinline="" height="100%" width="375"></video><div><button>Back</button><div><label htmlFor="uploadImg">Upload image:</label><input id="uploadImg" type="file" accept="image/*" style="display: none;"><canvas style="display: none;"></canvas></div></div><div className="textLog">Text Log</div></div> --> */}
          </div>
        </main>
        {showHowToPopup && (
          <HowToPopup onClose={() => setShowHowToPopup(false)} />
        )}
        {showStreakHowToPopup && (
          <StreakHowToPopup onClose={() => setShowStreakHowToPopup(false)} />
        )}
        <Menu />
      </div>
    </div>
  );
};

export default ScanPointNew;
